import { logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { getAnalyticsInstance } from '../firebase';
import { EventRSVPStatus } from '../hooks/useEvent';
import { storeAnalyticsSession } from '../endpoints/api';
import { User } from '../hooks/useAuth';
import { Event, Place, Offer }  from '../types/article';

// Types
export type AnalyticsIds = {
  user_pseudo_id: string | null;
  session_id: string | null;
  /** Platform discriminator — determines how user_pseudo_id is interpreted by the backend */
  platform: 'ios' | 'android' | 'web' | null;
};

// Store current user for analytics tracking
let currentUser: User | null = null;

// #region Events

/**
 * Log an analytics event.
 *
 * When running inside the React Native WebView, the event is forwarded to the
 * native Firebase SDK via postMessage so it is recorded in the App Data Stream
 * (which carries Google Ads install-referrer attribution).
 *
 * When running in a regular browser (no native shell), the event is sent via
 * the Firebase JS SDK / gtag as a fallback.
 *
 * @param {string} eventName - The name of the event
 * @param {Object} parameters - Event parameters to send
 * @returns {void}
 */
export const logAnalyticsEvent = (eventName: string, parameters: object = {}): void => {
  // Prefer native Firebase SDK when inside React Native WebView.
  // This routes all events through the App Data Stream for proper attribution.
  if (isRunningInReactNativeWebview()) {
    try {
      const analyticsPayload = {
        type: 'ANALYTICS_EVENT',
        eventName,
        parameters,
      };
      window['ReactNativeWebView'].postMessage(JSON.stringify(analyticsPayload));
      console.log('[Analytics] Event sent to native:', eventName, parameters);
      return;
    } catch (error) {
      console.warn('[Analytics] Failed to send to native, falling back to web SDK:', error);
      // Fall through to web SDK below
    }
  }

  // Fallback: use the Firebase JS SDK (web) for browser-only access
  if (!isAnalyticsAvailable()) {
    console.log(`[Analytics] Skipping event '${eventName}' - analytics unavailable`, parameters);
    return;
  }

  try {
    const analytics = getAnalyticsInstance();
    logEvent(analytics, eventName, parameters);
    console.log('[Analytics] Event logged (web):', eventName, parameters);
  } catch (error) {
    console.error('[Analytics] Error logging analytics event:', error);
  }
};

/**
 * Logs a search event when a user performs a search
 * @param {string} search_term - The search term used
 * @param {string} content_type - The type of content being searched (e.g., 'all', 'event', 'place', 'offer', 'post', 'partner')
 * @param {Object} additionalParams - Optional additional parameters
 */
export const logSearchEvent = (search_term: string, content_type: string, additionalParams: object = {}) => {
  logAnalyticsEvent('search', {
    search_term,
    content_type,
    ...additionalParams,
  });
};

/**
 * Track a share event when a user shares content from the app
 * @param {string} contentType - The type of content being shared (e.g., 'referral_link', 'event', 'place')
 * @param {string} method - The method used to share (e.g., 'copy_link', 'native_share', 'whatsapp', 'instagram')
 * @param {string|number} contentId - The ID of the item being shared (e.g., user referral code, event ID, place ID)
 * @param {Object} additionalParams - Optional additional parameters
 */
export const logShareEvent = (contentType: string, method: string, contentId: string | number, additionalParams: object = {}) => {
  logAnalyticsEvent('share', {
    content_id: contentId?.toString(),
    content_type: contentType,
    method,
    ...additionalParams,
  });
};

/**
 * Track when a user sends a chat message in any chat context
 * @param {string} content_type - The type of chat (e.g., 'event', 'community', 'place', 'lifestyle')
 * @param {string|number} content_id - The ID of the chat context (event ID, community ID, place ID, etc.)
 * @param {Object} additionalParams - Optional additional parameters
 */
export const logChatMessageSend = (content_type: string, content_id: string | number, additionalParams: object = {}) => {
  logAnalyticsEvent('chat_message_send', {
    content_type,
    content_id: content_id?.toString(),
    ...additionalParams,
  });
};

/**
 * Track a view_item event for an event page with consistent GA4 formatting
 * @param {Event} event - The event object
 * @param {Object} additionalParams - Optional additional parameters to include in the item
 */
export const logEventViewItem = (event: Event, additionalParams: object = {}) => {
  const price = event.price || 0;

  logAnalyticsEvent('view_item', {
    currency: 'USD',
    value: price,
    ...additionalParams,
    items: [{
      item_id: event.id,
      item_name: event.title,
      item_category: 'event',
      item_variant: event.is_walkabout_official ? 'Walkabout Official' : 'Community Event',
      price: price,
      currency: 'USD',
      quantity: 1,
      location_id: event.place?.google_place_id ?? event.place?.id?.toString(), // Use location_id for item scope
      is_recurring: event.is_recurring,
      has_tickets: event.has_tickets || false,
      is_premium: event.is_premium || false,
    }]
  });
};

/**
 * Track a view_item event for a place page with consistent GA4 formatting
 * @param {Place} place - The place object
 * @param {Object} additionalParams - Optional additional parameters to include in the item
 */
export const logPlaceViewItem = (place : Place, additionalParams = {}) => {
  logAnalyticsEvent('view_item', {
    currency: 'USD',
    value: 0, // Places typically don't have a price
    ...additionalParams,
    items: [{
      item_id: place.id,
      item_name: place.name,
      item_category: 'place',
      currency: 'USD',
      quantity: 1,
      location_id: place.google_place_id ?? place.id?.toString(), // Use location_id for item scope
    }]
  });
};

/**
 * Track an RSVP event when a user successfully RSVPs to an event
 * @param {number} eventId - The event ID
 * @param {number} rsvpStatus - The RSVP status (1=INTERESTED, 2=GOING, 3=NOT_GOING, 4=NOT_INTERESTED)
 */
export const logEventRSVP = (eventId: number, rsvpStatus: number) => {
  // Convert enum value to readable string
  const rsvpStatusText = Object.keys(EventRSVPStatus)
    .find(key => EventRSVPStatus[key] === rsvpStatus)?.toLowerCase() || 'unknown';

  logAnalyticsEvent('event_rsvp', {
    content_id: eventId,
    content_type: 'event',
    status: rsvpStatusText,
  });
};

/**
 * Track a check-in event when a user successfully checks in to a place or event
 * @param {Place} place - The place object
 * @param {boolean} viaQrCode - Whether check-in was done via QR code
 */
export const logPlaceCheckIn = (place: Place, viaQrCode: boolean = false) => {
  logAnalyticsEvent('place_check_in', {
    content_id: place.id,
    content_type: 'place',
    content_name: place.name,
    place_id: place.google_place_id ?? place.id?.toString(),
    method: viaQrCode ? 'QR Code' : 'App',
  });
};

/**
 * Track a check-in event when a user successfully checks in to a place or event
 * @param {Event} event - The event object
 * @param {boolean} viaQrCode - Whether check-in was done via QR code
 */
export const logEventCheckIn = (event: Event, viaQrCode: boolean = false) => {
  logAnalyticsEvent('event_check_in', {
    content_id: event.id,
    content_type: 'event',
    content_name: event.title,
    place_id: event.place?.google_place_id ?? event.place_id?.toString(),
    method: viaQrCode ? 'QR Code' : 'App',
  });
};

/**
 * Track a meetup creation event when a user successfully creates a meetup
 * @param {Event} event - The created meetup object
 * @param {number} audiencePreview - The audience preview number (optional)
 */
export const logCreateMeetup = (event: Event, audiencePreview: null|number = null) => {
  logAnalyticsEvent('create_meetup', {
    content_id: event.id,
    content_type: 'event',
    content_name: event.title,
    place_id: event.place?.google_place_id || null,
  });
};

/**
 * Track an offer redemption event when a user successfully redeems an offer
* @param {Offer} offer - The offer object that was redeemed
 */
export const logOfferRedemption = (offer: Offer) => {
  logAnalyticsEvent('redeem_offer', {
    content_id: offer.id.toString(),
    content_type: 'offer',
    content_name: offer.title,
    place_id: offer.place?.google_place_id || null,
  });
};

/**
 * Track a subscription cancellation event when a user cancels their auto-renewal
 * @param subscription
 */
export const logSubscriptionCancel = (subscription: any) => {
  if (!subscription) {
    return;
  }

  const now = new Date();
  const createdAt = new Date(subscription.created_at);
  const tenureDays = Math.floor((now.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24));

  logAnalyticsEvent('subscription_cancel', {
    tenure_days: tenureDays,
  });
};

/**
 * Track when a subscription has been resumed from a paused state.
 */
export const logSubscriptionResume = (subscription: any) => {
  logAnalyticsEvent('membership_resume_committed', {
    subscription_id: subscription?.id,
  });
};

/**
 * Set the user ID for analytics tracking (call this when user logs in/signs up)
 * @returns {void}
 */
export const setAnalyticsUserId = () => {
  if (!isAnalyticsAvailable()) {
    console.log('[Analytics] Skipping setAnalyticsUserId - analytics unavailable');
    return;
  }

  if (!currentUser?.id) {
    console.log('[Analytics] No user or user ID available - skipping setAnalyticsUserId');
    return;
  }

  try {
    const analytics = getAnalyticsInstance();
    setUserId(analytics, currentUser.id.toString());
    console.log('[Analytics] User ID set:', currentUser.id);
  } catch (error) {
    console.error('[Analytics] Error setting user ID:', error);
  }
};
// #endregion Events

// #region Utils

// Check if analytics is available
const isAnalyticsAvailable = () => {
  const analytics = getAnalyticsInstance();
  return analytics !== undefined && analytics !== null;
};

// Set user properties for Google Analytics
const setAnalyticsUserProperties = () => {
  if (!isAnalyticsAvailable() || !currentUser) {
    return;
  }

  try {
    const analytics = getAnalyticsInstance();
    const properties = {
      is_internal: currentUser.isInternal || false,
      subscription_plan: currentUser.activeMembershipPlan?.name || 'Free',
      sms_enabled: currentUser.smsEnabled || false,
      email_enabled: currentUser.emailEnabled || false,
      push_enabled: currentUser.pushEnabled || false,
      sign_up_date: currentUser.createdAt
          ? new Date(currentUser.createdAt).toISOString().split('T')[0] //"YYYY-MM-DD"
          : 'unknown',
    };
    console.log('[Analytics] Setting user properties:', properties);
    setUserProperties(analytics, properties);
    console.log('[Analytics] User properties set successfully');
  } catch (error) {
    console.error('[Analytics] Error setting user properties:', error);
  }
};

// Clear user ID and stored user data (call this when user logs out)
export const clearUserAnalytics = () => {
  // Clear stored user
  currentUser = null;
  
  if (!isAnalyticsAvailable()) {
    return;
  }

  try {
    const analytics = getAnalyticsInstance();
    setUserId(analytics, null);
    console.log('[Analytics] User cleared completely');
  } catch (error) {
    console.error('[Analytics] Error clearing analytics user:', error);
  }
};

/**
 * Get Google Analytics client_id and session_id for cross-platform tracking.
 *
 * When running inside the React Native WebView, the native Firebase SDK's
 * app_instance_id is used as the client_id. This ID carries Google Ads
 * install attribution from the Play Store Install Referrer API, ensuring
 * purchase events sent via Measurement Protocol link back to the original
 * ad click.
 *
 * For web-only access (no native shell), the browser gtag IDs are used.
 */
export const getAnalyticsIds = async (): Promise<AnalyticsIds> => {
  try {
    // Prefer the native app_instance_id when running in the React Native WebView.
    // The native Firebase SDK holds install-referrer attribution that the
    // WebView's own gtag session does not have.
    if (isRunningInReactNativeWebview() && window.__NATIVE_APP_INSTANCE_ID__) {
      console.log('[Analytics] Using native app_instance_id for attribution:', window.__NATIVE_APP_INSTANCE_ID__);
      return {
        user_pseudo_id: window.__NATIVE_APP_INSTANCE_ID__,
        session_id: getOrCreateSessionId(),
        platform: window.__NATIVE_APP_PLATFORM__ ?? null,
      };
    }

    // Web-only: retrieve IDs from gtag
    if (isAnalyticsAvailable() && typeof window !== 'undefined' && window.gtag && process.env.REACT_APP_FIREBASE_MEASUREMENT_ID) {
      return new Promise<AnalyticsIds>((resolve) => {
        let client_id: string | null = null;
        let session_id: string | null = null;
        let callsCompleted = 0;

        const checkComplete = () => {
          callsCompleted++;
          if (callsCompleted === 2) {
            console.log('[Analytics] GA IDs retrieved:', { user_pseudo_id: client_id, session_id });
            resolve({ user_pseudo_id: client_id, session_id, platform: 'web' });
          }
        };

        window.gtag!('get', process.env.REACT_APP_FIREBASE_MEASUREMENT_ID, 'client_id', (id: string) => {
          client_id = id;
          checkComplete();
        });

        window.gtag!('get', process.env.REACT_APP_FIREBASE_MEASUREMENT_ID, 'session_id', (id: string) => {
          session_id = id;
          checkComplete();
        });

        setTimeout(() => {
          if (callsCompleted < 2) {
            console.warn('[Analytics] Timeout getting GA IDs, using fallback');
            resolve({
              user_pseudo_id: client_id || `${Date.now()}.${Math.floor(Math.random() * 1000000000)}`,
              session_id: session_id || getOrCreateSessionId(),
              platform: 'web',
            });
          }
        }, 1000);
      });
    }

    console.log('[Analytics] gtag not available, using fallback for GA IDs');
    return {
      user_pseudo_id: `${Date.now()}.${Math.floor(Math.random() * 1000000000)}`,
      session_id: getOrCreateSessionId(),
      platform: 'web',
    };
  } catch (error) {
    console.error('[Analytics] Error getting analytics IDs:', error);
    return { user_pseudo_id: null, session_id: null, platform: null };
  }
};

const getOrCreateSessionId = (): string => {
  const generateSessionId = () =>
    `${Date.now()}_${Math.floor(Math.random() * 1000000)}`;

  try {
    if (typeof sessionStorage === 'undefined') {
      return generateSessionId();
    }

    let sessionId = sessionStorage.getItem('ga_session_id');
    if (!sessionId) {
      sessionId = generateSessionId();
      sessionStorage.setItem('ga_session_id', sessionId);
    }
    return sessionId;
  } catch {
    return generateSessionId();
  }
};

/**
 * Sync Google Analytics client_id and session_id with backend for cross-platform tracking
 * This should be called after login, registration, or when the app loads with an authenticated user
 * @returns {Promise<boolean>} - Returns true if sync was successful, false otherwise
 */
export const syncAnalyticsSession = async (): Promise<boolean> => {
  // Only sync when we have a meaningful analytics source (native ID or web SDK)
  if (!isAnalyticsAvailable() && !isRunningInReactNativeWebview()) {
    console.log('[Analytics] Skipping session sync - no analytics source available');
    return false;
  }

  try {
    const { user_pseudo_id, session_id, platform } = await getAnalyticsIds();

    // If we couldn't get valid IDs, don't sync
    if (!user_pseudo_id || !session_id) {
      console.warn('[Analytics] Could not get valid analytics IDs - skipping backend sync');
      return false;
    }

    // Call the backend API to store the analytics session
    const response = await storeAnalyticsSession({
      user_pseudo_id,
      session_id,
      ...(platform && { platform }),
    });

    if (response?.data?.success) {
      console.log('[Analytics] Session synced successfully with backend');
      return true;
    } else {
      console.warn('[Analytics] Session sync failed:', response?.data?.message);
      return false;
    }
  } catch (error) {
    console.error('[Analytics] Error syncing analytics session:', error);
    return false;
  }
};

/**
 * Initialize analytics for a user - stores user data and syncs session with backend
 * Call this once per session when user logs in or app loads with authenticated user
 * @param {Object} user - User object containing analytics data
 * @returns {Promise<boolean>} - Returns true if initialization was successful, false otherwise
 */
export const initializeUserAnalytics = async (user: null|User): Promise<boolean> => {
  if (!user) {
    console.warn('[Analytics] No user provided for initialization');
    return false;
  }

  // Check if we're already initialized for this user
  if (currentUser?.id === user.id) {
    console.log('[Analytics] Analytics already initialized for user:', user.id, '- skipping');
    return true;
  }

  // Store user for analytics functions to access
  currentUser = user;
  
  // Set Firebase Analytics user ID
  setAnalyticsUserId();
  
  // Set user properties for Google Analytics
  setAnalyticsUserProperties();

  // Sync session with backend
  return await syncAnalyticsSession();
};

/**
 * Send analytics event to React Native app via webview postMessage.
 *
 * @deprecated Use {@link logAnalyticsEvent} instead — it now automatically routes
 * events through the native Firebase SDK when running in a WebView.
 *
 * This function is kept for backward compatibility but simply delegates to
 * logAnalyticsEvent.
 *
 * @param {string} eventName - The name of the analytics event
 * @param {Object} parameters - Event parameters to send
 * @returns {void}
 */
export const sendAnalyticsEventToMobile = (eventName: string, parameters = {}) => {
  logAnalyticsEvent(eventName, parameters);
};

/**
 * Check if the app is running inside a React Native webview
 * @returns {boolean} - True if running in React Native webview, false otherwise
 */
export const isRunningInReactNativeWebview = () => {
  return typeof window !== 'undefined' && !!window.ReactNativeWebView;
};

// #endregion Utils
